import React, { useState } from "react";
import {
  Box, Typography, Paper, TextField, Button,
  FormControlLabel, Switch, InputAdornment, Avatar, CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SchoolIcon      from "@mui/icons-material/School";
import SaveIcon        from "@mui/icons-material/Save";
import { useSnackbar } from "notistack";
import { update_school_api, upload_logo_api } from "../../DAL/FlightSchools/FlightSchools";
import { baseUrl } from "../../config/config";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff", borderRadius: "9px", color: "#1a1a1a",
    "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
    "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
    "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { color: "#1a1a1a" },
  "& .MuiInputLabel-root": { color: "#aaa", fontSize: "14px" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#c01e2e" },
  "& .MuiFormHelperText-root": { color: "#aaa" },
  "& .MuiInputAdornment-root p": { color: "#6b6b6b" },
};

const cardSx = {
  p: 3,
  backgroundColor: "#ffffff",
  border: "1px solid rgba(26,26,26,0.09)",
  borderRadius: "14px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
};

const SectionTitle = ({ children }) => (
  <Typography sx={{ color: "#c01e2e", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", mb: 2.5, fontFamily: "'Rubik', sans-serif" }}>
    {children}
  </Typography>
);

const saveBtn = () => ({
  backgroundColor: "#c01e2e", textTransform: "none", fontFamily: "'Rubik', sans-serif",
  borderRadius: "8px", boxShadow: "none",
  "&:hover": { backgroundColor: "#a81828", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "rgba(192,30,46,0.5)" },
});

export default function FlightSchoolSettings({ schoolId, school, onUpdate }) {
  const { enqueueSnackbar } = useSnackbar();

  const [brandingData,  setBrandingData]  = useState({
    primary_color:   school?.branding?.primary_color   || "#c01e2e",
    secondary_color: school?.branding?.secondary_color || "#1e3a5f",
    accent_color:    school?.branding?.accent_color    || "#ffc107",
  });
  const [logoPreview,   setLogoPreview]   = useState(school?.branding?.logo ? baseUrl + school.branding.logo : null);
  const [logoFile,      setLogoFile]      = useState(null);
  const [brandingSaving, setBrandingSaving] = useState(false);

  const [seatsData, setSeatsData]   = useState({ included_seats: school?.included_seats || 10, monthly_student_fee: school?.monthly_student_fee || 28.99 });
  const [seatsSaving, setSeatsSaving] = useState(false);

  const [aiData, setAiData] = useState({
    charge_excess_usage: school?.ai_settings?.charge_excess_usage || false,
    voice_ai_price_per_minute: school?.ai_settings?.voice_ai_price_per_minute || 0.40,
    ground_instructor_price_per_request: school?.ai_settings?.ground_instructor_price_per_request || 0.10,
  });
  const [aiSaving, setAiSaving] = useState(false);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveBranding = async () => {
    setBrandingSaving(true);
    try {
      if (logoFile) {
        const fd = new FormData(); fd.append("logo", logoFile);
        const r = await upload_logo_api(schoolId, fd);
        if (r.code !== 200) { enqueueSnackbar(r.message || "Failed to upload logo", { variant: "error" }); setBrandingSaving(false); return; }
      }
      const result = await update_school_api(schoolId, { branding: { primary_color: brandingData.primary_color, secondary_color: brandingData.secondary_color, accent_color: brandingData.accent_color } });
      if (result.code === 200) { enqueueSnackbar("Branding updated successfully", { variant: "success" }); setLogoFile(null); onUpdate?.(); }
      else enqueueSnackbar(result.message || "Failed to update branding", { variant: "error" });
    } catch { enqueueSnackbar("Error updating branding", { variant: "error" }); }
    finally { setBrandingSaving(false); }
  };

  const handleSaveSeats = async () => {
    setSeatsSaving(true);
    try {
      const r = await update_school_api(schoolId, { included_seats: seatsData.included_seats, monthly_student_fee: seatsData.monthly_student_fee });
      if (r.code === 200) { enqueueSnackbar("Seat allocation updated successfully", { variant: "success" }); onUpdate?.(); }
      else enqueueSnackbar(r.message || "Failed to update seats", { variant: "error" });
    } catch { enqueueSnackbar("Error updating seat allocation", { variant: "error" }); }
    finally { setSeatsSaving(false); }
  };

  const handleSaveAi = async () => {
    setAiSaving(true);
    try {
      const r = await update_school_api(schoolId, { ai_settings: { charge_excess_usage: aiData.charge_excess_usage, voice_ai_price_per_minute: aiData.voice_ai_price_per_minute, ground_instructor_price_per_request: aiData.ground_instructor_price_per_request } });
      if (r.code === 200) { enqueueSnackbar("AI settings updated successfully", { variant: "success" }); onUpdate?.(); }
      else enqueueSnackbar(r.message || "Failed to update AI settings", { variant: "error" });
    } catch { enqueueSnackbar("Error updating AI settings", { variant: "error" }); }
    finally { setAiSaving(false); }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Branding */}
      <Paper sx={cardSx}>
        <SectionTitle>Branding (White Label)</SectionTitle>
        <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "flex-start" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <Avatar src={logoPreview} sx={{ width: 96, height: 96, backgroundColor: brandingData.primary_color, border: "2px solid rgba(26,26,26,0.12)" }}>
              <SchoolIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Button component="label" variant="outlined" size="small" startIcon={<CloudUploadIcon />}
              sx={{ borderColor: "rgba(26,26,26,0.2)", color: "#6b6b6b", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "7px", fontSize: "11px",
                "&:hover": { borderColor: "#c01e2e", color: "#c01e2e", backgroundColor: "rgba(192,30,46,0.05)" } }}>
              Change Logo <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
            </Button>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 3, flex: 1, minWidth: 200 }}>
            {[{ label: "Primary", field: "primary_color" }, { label: "Secondary", field: "secondary_color" }, { label: "Accent", field: "accent_color" }].map(({ label, field }) => (
              <Box key={field}>
                <Typography sx={{ color: "#aaa", fontSize: "11px", mb: 0.5, fontFamily: "'Rubik', sans-serif" }}>{label}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <input type="color" value={brandingData[field]} onChange={(e) => setBrandingData({ ...brandingData, [field]: e.target.value })}
                    style={{ width: 44, height: 36, border: "none", borderRadius: 8, cursor: "pointer" }} />
                  <TextField size="small" value={brandingData[field]} onChange={(e) => setBrandingData({ ...brandingData, [field]: e.target.value })}
                    sx={{ ...inputSx, width: 100 }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        {/* Preview */}
        <Box sx={{ mt: 3, p: 2.5, borderRadius: "12px", background: `linear-gradient(135deg, ${brandingData.primary_color}18, ${brandingData.secondary_color}18)`, border: `1px solid ${brandingData.primary_color}30` }}>
          <Typography sx={{ color: "#aaa", fontSize: "11px", mb: 1.5, fontFamily: "'Rubik', sans-serif", textTransform: "uppercase", letterSpacing: "0.4px" }}>Preview</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar src={logoPreview} sx={{ width: 44, height: 44, backgroundColor: brandingData.primary_color }}><SchoolIcon /></Avatar>
            <Box>
              <Typography sx={{ color: brandingData.primary_color, fontSize: "17px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>{school?.name || "Flight School Name"}</Typography>
              <Typography sx={{ color: brandingData.accent_color, fontSize: "11px", fontFamily: "'Rubik', sans-serif" }}>Powered by XAir</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button variant="contained" startIcon={brandingSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon />} onClick={handleSaveBranding} disabled={brandingSaving} sx={saveBtn()}>Save Branding</Button>
        </Box>
      </Paper>

      {/* Seat Allocation */}
      <Paper sx={cardSx}>
        <SectionTitle>Seat Allocation</SectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
          <TextField fullWidth label="Included Seats" type="number" value={seatsData.included_seats}
            onChange={(e) => setSeatsData({ ...seatsData, included_seats: parseInt(e.target.value) || 0 })} inputProps={{ min: 1 }} sx={inputSx} />
          <TextField fullWidth label="Monthly Student Fee" type="number" value={seatsData.monthly_student_fee}
            onChange={(e) => setSeatsData({ ...seatsData, monthly_student_fee: parseFloat(e.target.value) || 0 })}
            InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
            helperText="Default fee for new student accounts" sx={inputSx} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button variant="contained" startIcon={seatsSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon />} onClick={handleSaveSeats} disabled={seatsSaving} sx={saveBtn()}>Save Seat Allocation</Button>
        </Box>
      </Paper>

      {/* AI Settings */}
      <Paper sx={cardSx}>
        <SectionTitle>AI Usage & Charging</SectionTitle>
        <FormControlLabel
          control={<Switch checked={aiData.charge_excess_usage} onChange={(e) => setAiData({ ...aiData, charge_excess_usage: e.target.checked })}
            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#c01e2e" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#c01e2e" } }} />}
          label={
            <Box>
              <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>Charge for Excess AI Usage</Typography>
              <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>Excess usage beyond included limits will be billed to the school</Typography>
            </Box>
          }
          sx={{ mb: 3, alignItems: "flex-start" }}
        />
        {aiData.charge_excess_usage && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
            <TextField fullWidth label="Voice AI Price" type="number" value={aiData.voice_ai_price_per_minute}
              onChange={(e) => setAiData({ ...aiData, voice_ai_price_per_minute: parseFloat(e.target.value) || 0 })}
              InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment>, endAdornment: <InputAdornment position="end">/ min</InputAdornment> }}
              inputProps={{ step: 0.01 }} sx={inputSx} />
            <TextField fullWidth label="Ground Instructor AI Price" type="number" value={aiData.ground_instructor_price_per_request}
              onChange={(e) => setAiData({ ...aiData, ground_instructor_price_per_request: parseFloat(e.target.value) || 0 })}
              InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment>, endAdornment: <InputAdornment position="end">/ req</InputAdornment> }}
              inputProps={{ step: 0.01 }} sx={inputSx} />
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button variant="contained" startIcon={aiSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon />} onClick={handleSaveAi} disabled={aiSaving} sx={saveBtn()}>Save AI Settings</Button>
        </Box>
      </Paper>
    </Box>
  );
}
